import { Badge } from '@/components/ui/Badge/badge';
import { completeConfig } from '@/components/ui/data-table/config';
import { DataTable } from '@/components/ui/data-table/data-table';
import { DataTableConsumer } from '@/components/ui/data-table/types';
import { workordersTableConfig } from '@/config/table-configs';
import { useCurrentUser } from '@/features/users/users.hooks';
import { useFilterableTable } from '@/hooks/useFilterableTable';
import { usePanelFilterState } from '@/hooks/usePanelFilterState';
import { usePanelSelectionState } from '@/hooks/usePanelSelectionState';
import { usePersistedTableConfig } from '@/hooks/usePersistedTableConfig';
import { LINE_THROUGH_CLASSES } from '@/lib/styles';
import { cn } from '@/lib/utils';
import { ShareContent } from '@/utils/share';
import { Workorder } from '@certhon/models';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { workorderFields } from './workorders.fields';
import { getWorkorderActions } from './workorders.actions';

const emptyArray: Workorder[] = [];

const customColumns = [
  {
    id: 'summary',
    header: 'Workorder',
    enableSorting: false,
    cell: ({ row }) => {
      const { id, customer_name, assigned_user_id, completed } = row.original;
      return (
        <div
          className={cn(
            'flex min-w-0 items-center gap-2',
            completed && LINE_THROUGH_CLASSES,
          )}
        >
          <span className="text-muted-foreground flex-shrink-0 text-xs tabular-nums">
            W{id}
          </span>
          <span className="min-w-0 truncate">{customer_name || '-'}</span>
          {!assigned_user_id && (
            <Badge variant="destructive" className="flex-shrink-0 text-xs">
              Not assigned
            </Badge>
          )}
        </div>
      );
    },
  },
] as ColumnDef<Workorder>[];

const excludeColumns = ['recnum', 'description', 'signature_data'];

interface WorkorderTableProps extends DataTableConsumer<Workorder> {
  defaultFilter?: string;
  hoveredId?: number | null;
  onRowHover?: (id: string | number | null) => void;
  onFilteredDataChange?: (data: Workorder[]) => void;
}

export function WorkorderTable({
  data,
  isLoading,
  defaultFilter = 'progress is: "scheduled"',
  hoveredId,
  onRowHover,
  onFilteredDataChange,
}: WorkorderTableProps) {
  const { filterValue, setFilterValue } = usePanelFilterState({
    prefix: 'wo.',
    defaultFilter,
  });

  const { rowSelection, setRowSelection, clearSelection } =
    usePanelSelectionState({ prefix: 'wo.' });

  // Clear selection when filter changes
  const prevFilterRef = useRef(filterValue);
  useEffect(() => {
    if (prevFilterRef.current !== filterValue) {
      clearSelection();
      prevFilterRef.current = filterValue;
    }
  }, [filterValue, clearSelection]);

  // Use schema-driven filter system
  const {
    tokenDefinitions,
    filteredData,
    columns,
    isFiltering,
    resultCount,
    totalCount,
  } = useFilterableTable<Workorder>({
    fields: workorderFields,
    data: data || emptyArray,
    filterValue,
    customColumns,
    excludeColumns,
  });

  // Notify parent of filtered data changes (for map sync)
  useEffect(() => {
    onFilteredDataChange?.(filteredData);
  }, [filteredData, onFilteredDataChange]);

  const completeDefaultConfig = useMemo(
    () => completeConfig(workordersTableConfig, columns),
    [columns],
  );

  const [tableConfig, setTableConfig] = usePersistedTableConfig(
    'workorders',
    completeDefaultConfig,
  );

  const createUriPath = useCallback(
    (wo: Workorder): string => `/workorders/${wo.recnum}`,
    [],
  );

  const createShareContent = useCallback(
    (wo: Workorder, url: string): ShareContent => ({
      title: `Workorder #${wo.id}: ${wo.description || 'No description'}`,
      text: wo.customer_name
        ? `Workorder for ${wo.customer_name}`
        : `Workorder #${wo.id}`,
      url,
    }),
    [],
  );

  const getActions = useCallback(
    (workorder: Workorder) => getWorkorderActions(undefined, workorder),
    [],
  );

  return (
    <DataTable<Workorder, unknown>
      isLoading={isLoading}
      data={filteredData}
      columns={columns}
      config={tableConfig}
      onConfigChange={setTableConfig}
      createUriPath={createUriPath}
      createShareContent={createShareContent}
      actions={getActions}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      tokenDefinitions={tokenDefinitions}
      isFiltering={isFiltering}
      resultCount={resultCount}
      totalCount={totalCount}
      entityName="workorders"
      defaultFilter={defaultFilter}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      hoveredRowId={hoveredId}
      onRowHover={onRowHover}
    />
  );
}
